import { colors, shadows, space } from '@ardoq/design-tokens';
import { TooltipContentCss } from '@ardoq/tooltip';
import { AgGridReact } from 'ag-grid-react';
import { useMemo } from 'react';
import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';
import {
  UseControlledAGGridProps,
  useControlledAGGrid,
} from './useControlledAGGrid';
import { AnyRow } from './types';
import { text2 } from '@ardoq/typography';
import { DEFAULT_TABLE_EMPTY_STATE_LABELS } from '../labels';
import { TableLoadingOverlay, renderTableEmptyState } from '../tableOverlays';

export type TableProps<
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
> = UseControlledAGGridProps<RowData, FullWidthRowData, RowId>;

export const Table = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: TableProps<RowData, FullWidthRowData, RowId>
) => {
  const emptyStateLabels = useMemo(
    () => ({
      ...DEFAULT_TABLE_EMPTY_STATE_LABELS,
      ...props.emptyStateLabels,
    }),
    [props.emptyStateLabels]
  );
  const defaultRenderEmptyState = useMemo(
    () => () => renderTableEmptyState(emptyStateLabels),
    [emptyStateLabels]
  );
  const { containerRef, ...gridProps } = useControlledAGGrid({
    ...props,
    renderEmptyState: props.renderEmptyState ?? defaultRenderEmptyState,
  });

  const grid = (
    <AgGridReact<AnyRow<RowData, FullWidthRowData, RowId>>
      {...gridProps}
      theme={theme}
      loading={props.loading}
      loadingOverlayComponent={TableLoadingOverlay}
    />
  );

  // When no explicit height is provided, the wrapper must not affect layout —
  // AG Grid expects its parent to constrain its size. `display: contents`
  // removes the wrapper's box while keeping the element in the DOM so the
  // outside-click handler can use `contains` against it.
  const wrapperStyle: React.CSSProperties = props.height
    ? { height: props.height }
    : { display: 'contents' };

  return (
    <>
      <TableStyles />
      <div ref={containerRef} style={wrapperStyle}>
        {grid}
      </div>
    </>
  );
};

export const TableStyles = createGlobalStyle`
  .ag-root-wrapper {
    isolation: isolate;
  }

  /* AG Grid appends the drag image component's root div — a plain wrapper
     around .ag-dnd-ghost — to <body>. Both chase the cursor and would
     otherwise swallow hover/hit-testing on drop targets (e.g. external drop
     zones) during a drag. The wrapper selector is scoped to a direct child
     of <body> so it can never match <body> itself. */
  .ag-dnd-ghost,
  body > :has(> .ag-dnd-ghost) {
    pointer-events: none;
  }

 .ag-tooltip-custom, .ag-tooltip {
    ${TooltipContentCss}
  }

  .ag-menu-option.ag-menu-option-disabled {
    cursor: default;
  }

  .ag-column-menu-visible {
    background-color: ${colors.surfaceHover};
  }

  .ag-column-menu-visible .ag-header-cell-menu-button {
    background-color: var(--surface-action-ghost-pressed);
    border-radius: var(--ag-icon-button-border-radius);
    box-shadow: 0 0 0 var(--ag-icon-button-background-spread) var(--surface-action-ghost-pressed);
    color: var(--icon-strong);
  }

  .ag-header-cell-align-center .ag-header-cell-label {
    justify-content: center;
  }

  .ag-cell-align-center {
    text-align: center;
  }

  .ag-header-cell-align-right {
    .ag-header-cell-label {
      flex-direction: row-reverse;
    }

    .ag-sort-indicator-icon {
      padding-left: 0;
      padding-right: var(--ag-spacing);
    }
  }

  .ag-cell-align-right {
    text-align: right;
  }

  .ag-row-error {
    border-left: 2px solid ${colors.borderErrorStrong};
    box-sizing: border-box;
  }

  .ag-row-warning {
    border-left: 2px solid ${colors.borderWarningStrong};
    box-sizing: border-box;
  }

  .ag-row-highlight {
    background-color: ${colors.surfaceInfo};
  }

  /* Highlight takes precedence over selection */
  .ag-row-selected.ag-row-highlight:before {
    background-color: ${colors.surfaceInfo};
  }

  /* Context Menu Styling to match DropdownMenu */
  .ag-menu {
    background-color: ${colors.surfaceDefault};
    color: ${colors.textDefault};
    box-shadow: ${shadows.shadowM};
  }
  .ag-menu-list {
    padding: ${space.small};
  }
  .ag-checkbox-input-wrapper {
    --ag-icon-size: 15px;
  }
  .ag-indeterminate {
    border-color: ${colors.iconActionActive};
  }
  .ag-menu-option {
    ${text2}
    border-radius: 6px;
    display: block;
  }
  .ag-menu-option:hover {
    background-color: ${colors.surfaceHover};
  }
  .ag-menu-list > .ag-menu-option {
    margin-top: 0;
  }
  .ag-menu-list > .ag-menu-option ~ .ag-menu-option {
    margin-top: ${space.xsmall};
  }
  .ag-menu-option-icon {
    padding-left: ${space.small};
  }
  .ag-menu-option-popup-pointer {
    padding-right: ${space.small};
  }
  .ag-menu-option-part {
    padding-top: calc(${space.small} - 1px);
    padding-bottom: calc(${space.small} - 1px);
  }
  .ag-menu-option-text {
    padding-left: ${space.small};
  }
  .ag-menu-separator {
    height: calc(${space.small} + 1px);
  }

  .ag-row-selected.ag-row-hover:before {
    background-color: ${colors.blue100};
    background-image: none;
  }

  .ag-row-highlight-inside {
    background-color: ${colors.surfaceHover};
  }

  .ag-row-highlight-inside::after {
    all: unset;
  }

  /* Hide default AG Grid drag handle in auto-group column - we use a custom one in GroupCellRenderer */
  .ag-cell[col-id="ag-Grid-AutoColumn"] .ag-row-drag {
    display: none;
  }

  /* Color + cursor for the grip icon. The shape itself is configured via the
     theme's iconOverrides (see theme.ts) as a mask-image. */
  .ag-icon-grip {
    color: ${colors.grey500};
    cursor: grab;
  }

  .ag-row-drag:hover .ag-icon-grip {
    color: ${colors.grey700};
  }

  .ag-cell:not(.ag-cell-auto-height) .ag-cell-wrapper {
    height: 100%;
  }
`;
